// VIBRANIAM-SPACE v2 - Pipeline.js
//
// Layer stack with per-layer feedback and bloom, then one final grade.
// Render targets use whatever HDR-capable type this GPU/driver actually
// renders to (see detectRenderTargetType below) so the trail can carry
// values above 1 without clipping, which is what makes bloom look like
// light rather than like paint.

import * as THREE from 'three';
import {
  QUAD_VERT, FEEDBACK_FRAG, THRESHOLD_FRAG, BLUR_FRAG, COMBINE_FRAG, FINAL_FRAG, STEREO_FRAG,
} from './shaders.js';

/**
 * HalfFloatType render targets report as supported (EXT_color_buffer_half_float
 * present) on some WebGL2/ANGLE/D3D11 stacks, yet silently render nothing -
 * no GL error, no console warning, every draw call into them a no-op. Picking
 * blind would make the whole engine produce a black canvas on exactly those
 * machines. Render a single opaque pixel into each candidate type and read it
 * back; use the first one that actually shows up, falling back toward
 * lower-precision (but always at least visible) options.
 */
function canRenderToType(renderer, type) {
  let rt;
  try {
    rt = new THREE.WebGLRenderTarget(2, 2, {
      format: THREE.RGBAFormat, type, depthBuffer: false, stencilBuffer: false,
    });
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.MeshBasicMaterial({ color: 0xffffff }));
    scene.add(mesh);

    const prevTarget = renderer.getRenderTarget();
    renderer.setRenderTarget(rt);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, true);
    renderer.render(scene, camera);
    renderer.setRenderTarget(prevTarget);

    // readRenderTargetPixels needs a buffer matching the target's actual type:
    // Uint8Array for UnsignedByteType, Uint16Array for the raw half-float bit
    // patterns of HalfFloatType (there is no native Float16Array), Float32Array
    // for FloatType. Passing the wrong one throws INVALID_OPERATION and leaves
    // the buffer untouched, which reads as "nothing rendered" even when it did.
    const buf = type === THREE.UnsignedByteType ? new Uint8Array(16)
      : type === THREE.HalfFloatType ? new Uint16Array(16)
      : new Float32Array(16);
    renderer.readRenderTargetPixels(rt, 0, 0, 2, 2, buf);

    mesh.geometry.dispose();
    mesh.material.dispose();

    let sum = 0;
    for (let i = 0; i < buf.length; i += 4) sum += buf[i] + buf[i + 1] + buf[i + 2];
    return sum > 0;
  } catch {
    return false;
  } finally {
    rt?.dispose();
  }
}

const detectedTypes = new WeakMap(); // renderer -> chosen THREE type constant

function detectRenderTargetType(renderer) {
  if (detectedTypes.has(renderer)) return detectedTypes.get(renderer);
  const candidates = [THREE.HalfFloatType, THREE.FloatType, THREE.UnsignedByteType];
  const chosen = candidates.find((type) => canRenderToType(renderer, type)) ?? THREE.UnsignedByteType;
  if (chosen === THREE.UnsignedByteType) {
    console.warn('[Pipeline] Nessun render target HDR disponibile su questa GPU: bloom e feedback useranno 8 bit, con possibile clipping.');
  }
  detectedTypes.set(renderer, chosen);
  return chosen;
}

function makeRT(renderer, w, h) {
  const rt = new THREE.WebGLRenderTarget(Math.max(1, w), Math.max(1, h), {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
    type: detectRenderTargetType(renderer),
    depthBuffer: false,
    stencilBuffer: false,
  });
  rt.texture.generateMipmaps = false;
  rt.texture.wrapS = THREE.ClampToEdgeWrapping;
  rt.texture.wrapT = THREE.ClampToEdgeWrapping;
  return rt;
}

/** A full-screen triangle-pair used by every post pass. */
class QuadPass {
  constructor() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.geometry = new THREE.PlaneGeometry(2, 2);
    this.mesh = new THREE.Mesh(this.geometry, null);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  render(renderer, material, target) {
    this.mesh.material = material;
    renderer.setRenderTarget(target || null);
    renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.geometry.dispose();
  }
}

const BLOOM_LEVELS = 3;

/** Per-layer resources: scene buffer, feedback ping-pong, bloom mips. */
class LayerBuffers {
  constructor(renderer, w, h) {
    this.scene = makeRT(renderer, w, h);
    this.fbA = makeRT(renderer, w, h);
    this.fbB = makeRT(renderer, w, h);
    this.out = makeRT(renderer, w, h);
    this.bloom = [];
    for (let i = 0; i < BLOOM_LEVELS; i++) {
      const d = 2 << i; // 2, 4, 8
      this.bloom.push({ a: makeRT(renderer, w / d, h / d), b: makeRT(renderer, w / d, h / d) });
    }
    this.primed = false;
  }

  setSize(w, h) {
    this.scene.setSize(w, h);
    this.fbA.setSize(w, h);
    this.fbB.setSize(w, h);
    this.out.setSize(w, h);
    for (let i = 0; i < BLOOM_LEVELS; i++) {
      const d = 2 << i;
      this.bloom[i].a.setSize(Math.max(1, Math.floor(w / d)), Math.max(1, Math.floor(h / d)));
      this.bloom[i].b.setSize(Math.max(1, Math.floor(w / d)), Math.max(1, Math.floor(h / d)));
    }
    this.primed = false;
  }

  swapFeedback() {
    const t = this.fbA;
    this.fbA = this.fbB;
    this.fbB = t;
  }

  dispose() {
    this.scene.dispose(); this.fbA.dispose(); this.fbB.dispose(); this.out.dispose();
    for (const b of this.bloom) { b.a.dispose(); b.b.dispose(); }
  }
}

export class Pipeline {
  constructor(renderer, width, height) {
    this.renderer = renderer;
    this.width = Math.max(2, Math.floor(width));
    this.height = Math.max(2, Math.floor(height));
    this.quad = new QuadPass();
    this.layers = new Map();     // layerId -> LayerBuffers
    this.composite = makeRT(this.renderer, this.width, this.height);
    this.stereoRT = makeRT(this.renderer, this.width, this.height);

    this.matFeedback = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERT, fragmentShader: FEEDBACK_FRAG, depthTest: false, depthWrite: false,
      uniforms: {
        uScene: { value: null }, uPrev: { value: null },
        uDecay: { value: 0.9 }, uZoom: { value: 1.0 }, uRotate: { value: 0.0 },
        uWarp: { value: 0.0 }, uTime: { value: 0.0 }, uAspect: { value: 1.0 },
      },
    });

    this.matThreshold = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERT, fragmentShader: THRESHOLD_FRAG, depthTest: false, depthWrite: false,
      uniforms: { uTex: { value: null }, uThreshold: { value: 0.5 }, uSoft: { value: 0.35 } },
    });

    this.matBlur = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERT, fragmentShader: BLUR_FRAG, depthTest: false, depthWrite: false,
      uniforms: {
        uTex: { value: null }, uTexel: { value: new THREE.Vector2() },
        uDir: { value: new THREE.Vector2(1, 0) }, uRadius: { value: 1 },
      },
    });

    this.matCombine = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERT, fragmentShader: COMBINE_FRAG, depthTest: false, depthWrite: false,
      uniforms: {
        uBase: { value: null },
        uBloom0: { value: null }, uBloom1: { value: null }, uBloom2: { value: null },
        uBloom: { value: 1 }, uOpacity: { value: 1 },
      },
    });

    this.matFinal = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERT, fragmentShader: FINAL_FRAG, depthTest: false, depthWrite: false,
      uniforms: {
        uTex: { value: null }, uBackground: { value: new THREE.Color(0x05060a) },
        uExposure: { value: 1 }, uVignette: { value: 0.35 }, uGrain: { value: 0.015 },
        uFrame: { value: 0 }, uAspect: { value: 1 },
      },
    });

    this.matStereo = new THREE.ShaderMaterial({
      vertexShader: QUAD_VERT, fragmentShader: STEREO_FRAG, depthTest: false, depthWrite: false,
      uniforms: {
        uTex: { value: null },
        uStrength: { value: 0 },
        uMode: { value: 0 },
        uFrame: { value: 0 },
        uAspect: { value: 1 },
      },
    });
  }

  setSize(width, height) {
    const w = Math.max(2, Math.floor(width));
    const h = Math.max(2, Math.floor(height));
    if (w === this.width && h === this.height) return;
    this.width = w;
    this.height = h;
    this.composite.setSize(w, h);
    this.stereoRT.setSize(w, h);
    for (const buf of this.layers.values()) buf.setSize(w, h);
  }

  buffersFor(layerId) {
    let b = this.layers.get(layerId);
    if (!b) {
      b = new LayerBuffers(this.renderer, this.width, this.height);
      this.layers.set(layerId, b);
    }
    return b;
  }

  releaseUnused(activeIds) {
    for (const [id, buf] of [...this.layers.entries()]) {
      if (!activeIds.has(id)) { buf.dispose(); this.layers.delete(id); }
    }
  }

  /**
   * @param {Array} layers  [{ id, scene, camera, params }]
   * @param {Object} output resolved r.output params
   * @param {number} frame  frame index (grain seed, feedback time)
   * @param {WebGLRenderTarget|null} target
   */
  render(layers, output, frame, fps, target = null) {
    const renderer = this.renderer;
    const aspect = this.width / this.height;
    const time = frame / (fps || 60);

    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    // Composite starts empty; layers accumulate additively.
    renderer.setRenderTarget(this.composite);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, true);

    for (const layer of layers) {
      const p = layer.params;
      const buf = this.buffersFor(layer.id);

      // 1. substrates into the layer's scene buffer
      renderer.setRenderTarget(buf.scene);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, true, true);
      if (layer.scene && layer.camera) renderer.render(layer.scene, layer.camera);

      // 2. feedback: scene over the warped previous frame
      const decay = Math.min(0.995, Math.max(0, p.feedback ?? 0));
      let colorRT;
      if (decay > 0.0005) {
        if (!buf.primed) {
          renderer.setRenderTarget(buf.fbA);
          renderer.setClearColor(0x000000, 1);
          renderer.clear(true, true, true);
          buf.primed = true;
        }
        const u = this.matFeedback.uniforms;
        u.uScene.value = buf.scene.texture;
        u.uPrev.value = buf.fbA.texture;
        u.uDecay.value = decay;
        u.uZoom.value = p.feedbackZoom ?? 1;
        u.uRotate.value = p.feedbackRotate ?? 0;
        u.uWarp.value = p.feedbackWarp ?? 0;
        u.uTime.value = time;
        u.uAspect.value = aspect;
        this.quad.render(renderer, this.matFeedback, buf.fbB);
        buf.swapFeedback();
        colorRT = buf.fbA;
      } else {
        buf.primed = false;
        colorRT = buf.scene;
      }

      // 3. bloom mips
      const bloomAmount = p.bloom ?? 0;
      if (bloomAmount > 0.001) {
        this.matThreshold.uniforms.uTex.value = colorRT.texture;
        this.matThreshold.uniforms.uThreshold.value = p.bloomThreshold ?? 0.4;
        this.quad.render(renderer, this.matThreshold, buf.bloom[0].a);

        for (let i = 0; i < BLOOM_LEVELS; i++) {
          const lvl = buf.bloom[i];
          if (i > 0) {
            // downsample from the previous level
            this.matThreshold.uniforms.uTex.value = buf.bloom[i - 1].a.texture;
            this.matThreshold.uniforms.uThreshold.value = 0.0;
            this.quad.render(renderer, this.matThreshold, lvl.a);
          }
          const w = lvl.a.width, h = lvl.a.height;
          const radius = (p.bloomRadius ?? 0.4) * 2.0;

          this.matBlur.uniforms.uTex.value = lvl.a.texture;
          this.matBlur.uniforms.uTexel.value.set(1 / w, 1 / h);
          this.matBlur.uniforms.uDir.value.set(1, 0);
          this.matBlur.uniforms.uRadius.value = radius;
          this.quad.render(renderer, this.matBlur, lvl.b);

          this.matBlur.uniforms.uTex.value = lvl.b.texture;
          this.matBlur.uniforms.uDir.value.set(0, 1);
          this.quad.render(renderer, this.matBlur, lvl.a);
        }
      }

      // 4. layer colour + bloom, scaled by opacity
      const c = this.matCombine.uniforms;
      c.uBase.value = colorRT.texture;
      c.uBloom0.value = bloomAmount > 0.001 ? buf.bloom[0].a.texture : null;
      c.uBloom1.value = bloomAmount > 0.001 ? buf.bloom[1].a.texture : null;
      c.uBloom2.value = bloomAmount > 0.001 ? buf.bloom[2].a.texture : null;
      c.uBloom.value = bloomAmount > 0.001 ? bloomAmount : 0;
      c.uOpacity.value = p.opacity ?? 1;
      this.quad.render(renderer, this.matCombine, buf.out);

      // 5. accumulate into the composite
      this.matCombine.uniforms.uBase.value = buf.out.texture;
      this.matCombine.uniforms.uBloom.value = 0;
      this.matCombine.uniforms.uOpacity.value = 1;
      this.matCombine.blending =
        p.blend === 'alpha' ? THREE.NormalBlending
        : p.blend === 'screen' ? THREE.CustomBlending
        : THREE.AdditiveBlending;
      if (p.blend === 'screen') {
        this.matCombine.blendEquation = THREE.AddEquation;
        this.matCombine.blendSrc = THREE.OneFactor;
        this.matCombine.blendDst = THREE.OneMinusSrcColorFactor;
      }
      this.matCombine.transparent = true;
      this.quad.render(renderer, this.matCombine, this.composite);
      this.matCombine.blending = THREE.NoBlending;
      this.matCombine.transparent = false;
    }

    // 6. grade
    const f = this.matFinal.uniforms;
    f.uTex.value = this.composite.texture;
    f.uExposure.value = output.exposure ?? 1;
    f.uVignette.value = output.vignette ?? 0;
    f.uGrain.value = output.grain ?? 0;
    f.uFrame.value = frame % 8192;
    f.uAspect.value = aspect;
    f.uBackground.value.set(output.background || '#05060a');

    // 7. stereo post-process (if enabled)
    const stereoMode = { off: 0, anaglyph: 1, sbs: 2, wiggle: 3 }[output.stereo ?? 'off'] || 0;
    const s = this.matStereo.uniforms;
    s.uStrength.value = output.stereoStrength ?? 0;
    s.uMode.value = stereoMode;
    s.uFrame.value = frame % 8192;
    s.uAspect.value = aspect;

    renderer.setRenderTarget(this.stereoRT);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, true);
    this.quad.render(renderer, this.matFinal, this.stereoRT); // grade to stereoRT

    s.uTex.value = this.stereoRT.texture;
    renderer.setRenderTarget(target);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, true);
    this.quad.render(renderer, this.matStereo, target);      // apply stereo to target

    renderer.autoClear = prevAutoClear;
    renderer.setRenderTarget(null);
  }

  /** Clears every feedback buffer - used when seeking or restarting. */
  resetFeedback() {
    for (const buf of this.layers.values()) buf.primed = false;
  }

  dispose() {
    for (const buf of this.layers.values()) buf.dispose();
    this.layers.clear();
    this.composite.dispose();
    this.stereoRT.dispose();
    this.quad.dispose();
    this.matFeedback.dispose();
    this.matThreshold.dispose();
    this.matBlur.dispose();
    this.matCombine.dispose();
    this.matFinal.dispose();
    this.matStereo.dispose();
  }
}
